'use client'

import { cn } from '@brew/ui/cn'
import { DialogClose, DialogDescription, DialogTitle } from '@brew/ui/dialog'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import { XIcon } from './icons'

/**
 * Dark media viewers (asset viewer, walkthrough video): a near-black stage
 * with light round controls, light pills and one orange primary. The
 * content carries `data-dialog-viewer` so the `--dlg-*` tokens resolve
 * without the light skin's control restyling.
 */

export const BREW_VIEWER_SURFACE_CLASS =
  'bg-[var(--dlg-viewer-bg)] text-[var(--dlg-viewer-ink)]'

/** Round controls on the dark stage (close, arrows, ⋯). */
export const BREW_VIEWER_ICON_BUTTON_CLASS =
  'flex size-[34px] shrink-0 items-center justify-center rounded-full bg-white/10 text-[var(--dlg-viewer-ink)] outline-none transition-colors hover:bg-white/[0.16] focus-visible:ring-[3px] focus-visible:ring-white/40 aria-disabled:cursor-default aria-disabled:opacity-30 aria-disabled:hover:bg-white/10'

/** Light pills: secondary actions on the dark stage. */
export const BREW_VIEWER_PILL_CLASS =
  'h-[34px] gap-2 rounded-full bg-white/[0.12] px-4 font-medium text-[var(--dlg-viewer-ink)] tracking-[-0.01em] hover:bg-white/20 hover:text-white has-[>svg]:px-4'

/** The one orange gradient pill: the viewer's primary action. */
export const BREW_VIEWER_PRIMARY_CLASS =
  'h-[34px] rounded-full border-[1.5px] border-[rgb(30_30_30/0.15)] bg-[image:var(--dlg-brand-gradient)] px-4 font-medium text-white tracking-[-0.01em] shadow-[0_1px_2px_rgb(40_40_40/0.08),inset_0_1px_0_rgb(255_255_255/0.22)] hover:brightness-105'

/** Close › title / description on the left, actions on the right. */
export function BrewViewerHeader({
  title,
  description,
  actions,
  closeLabel,
  className,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** The close control's name; "Close viewer", translated, by default. */
  closeLabel?: string
  className?: string
}) {
  const t = useTranslations('ui')
  return (
    <div
      className={cn(
        'flex h-[68px] shrink-0 items-center justify-between gap-4 px-5',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-3.5">
        <DialogClose
          aria-label={closeLabel ?? t('brewDialog.closeViewer')}
          className={cn(BREW_VIEWER_ICON_BUTTON_CLASS, 'size-9')}
        >
          <XIcon className="size-4" strokeWidth={2} aria-hidden="true" />
        </DialogClose>
        <div className="flex min-w-0 flex-col">
          <DialogTitle className="truncate font-medium text-[var(--dlg-viewer-ink)] text-sm">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="truncate text-[var(--dlg-viewer-muted)] text-xs">
              {description}
            </DialogDescription>
          ) : null}
        </div>
      </div>
      {actions}
    </div>
  )
}
